import React from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { ChannelCount } from "./chatAnalyticsService";
import {
  GRID_COLOR,
  SOURCE_COLORS,
  renderTooltipCard,
  renderTooltipHeader,
  renderTooltipRow,
} from "./chatAnalyticsPalette";

interface ChatChannelChartProps {
  channels: ChannelCount[];
}

// Agent replies and bot replies are both subsets of a channel's total
// messages (not separate totals) — plotting them as three independent bars
// (as an earlier version of this chart did) double-counts and makes every
// bar's hover tooltip read identically regardless of which bar you're on.
// Stacking "customer messages" + "agent replies" + "bot replies" instead
// makes the stack's full height equal the real total_messages, and each
// segment is an honest share of that one number.
const ChatChannelChart: React.FC<ChatChannelChartProps> = ({ channels }) => {
  if (channels.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "320px" }}>
        <div className="text-center">
          <i className="ti ti-antenna-bars-off fs-1 text-muted mb-3" />
          <p className="text-muted mb-0">No channel data for the selected filters</p>
        </div>
      </div>
    );
  }

  const categories = channels.map((c) => c.external_chat_channel);
  const customerMessages = channels.map((c) =>
    Math.max(0, c.total_messages - c.total_agent_replies - c.total_bot_replies)
  );

  const series = [
    { name: "Customer messages", data: customerMessages },
    { name: "Agent replies", data: channels.map((c) => c.total_agent_replies) },
    { name: "Bot replies", data: channels.map((c) => c.total_bot_replies) },
  ];
  const seriesColors = [SOURCE_COLORS.customer, SOURCE_COLORS.agent, SOURCE_COLORS.bot];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 340,
      stacked: true,
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: seriesColors,
    plotOptions: {
      bar: {
        borderRadius: 6,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        columnWidth: "48%",
      },
    },
    fill: {
      type: "gradient",
      gradient: { shade: "light", type: "vertical", shadeIntensity: 0.25, opacityFrom: 1, opacityTo: 0.88 },
    },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    grid: { borderColor: GRID_COLOR, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { fontWeight: 600 } },
    },
    yaxis: { min: 0, labels: { formatter: (val: number) => Math.round(val).toString() } },
    legend: { position: "bottom", fontWeight: 600, markers: { size: 7 } },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex }) => {
        const channel = channels[dataPointIndex];
        const rows = [
          { label: "Customer messages", value: customerMessages[dataPointIndex], color: SOURCE_COLORS.customer },
          { label: "Agent replies", value: channel.total_agent_replies, color: SOURCE_COLORS.agent },
          { label: "Bot replies", value: channel.total_bot_replies, color: SOURCE_COLORS.bot },
        ];
        return renderTooltipCard(
          renderTooltipHeader(channel.external_chat_channel, channel.total_messages) +
            rows
              .map((r, i) => renderTooltipRow(r.label, r.value, r.color, i === seriesIndex))
              .join("")
        );
      },
    },
    states: { hover: { filter: { type: "darken" } } },
  };

  return <Chart options={options} series={series} type="bar" height={340} />;
};

export default ChatChannelChart;
